import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Chart as ChartJS } from 'chart.js';

import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import ActivityBar from './components/ActivityBar';
import ReasonPrompt from './components/ReasonPrompt';
import PrivateRoute from './components/PrivateRoute';
import ApprovalProvider from './components/ApprovalProvider';
import ApprovalDecisionPage from './components/ApprovalDecisionPage';
import ApprovalsPage from './components/ApprovalsPage';
import RecoveryPage from './components/RecoveryPage';
import { LockdownBanner } from './components/EmergencyControls';
import LoginForm from './components/LoginForm';
import SalesDashboard from './components/SalesDashboard';
import ClientsPage from './components/ClientsPage';
import ProductsPage from './components/ProductsPage';
import GastosPage from './components/GastosPage';
import ReportsPage from './components/ReportsPage';
import ReportPrintView from './components/ReportPrintView';
import InvoicesPage from './components/InvoicesPage';
import InvoiceBuilder from './components/InvoiceBuilder';
import IngresosPage from './components/IngresosPage';
import ConducesPage from './components/ConducesPage';
import ConduceBuilder from './components/ConduceBuilder';
import ConducePrintView from './components/ConducePrintView';
import IngresoPrintView from './components/IngresoPrintView';
import InvoicePrintView from './components/InvoicePrintView';
import QuotesPage from './components/QuotesPage';
import QuoteBuilder from './components/QuoteBuilder';
import QuotePrintView from './components/QuotePrintView';
import AlquileresPage from './components/AlquileresPage';
import RentalBuilder from './components/RentalBuilder';
import RentalContractPrintView from './components/RentalContractPrintView';
import WhatsAppInboxPage from './components/WhatsAppInboxPage';
import AdminDashboard from './components/AdminDashboard';
import ChangePasswordPage from './components/ChangePasswordPage';

ChartJS.defaults.color = '#9DB3C2';
ChartJS.defaults.borderColor = 'rgba(234,242,245,0.10)';
ChartJS.defaults.font.family = "'DM Sans', sans-serif";

function AppRoutes() {
  const { isLoggedIn, mustChangePassword } = useAuth();
  const location = useLocation();

  // Until they choose their own password, nothing else is reachable.
  if (isLoggedIn && mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginForm />} />
      <Route path="/aprobacion/:token" element={<ApprovalDecisionPage />} />
      <Route path="/recuperar" element={<RecoveryPage />} />

      <Route path="/dashboard" element={<PrivateRoute><SalesDashboard /></PrivateRoute>} />
      <Route path="/clients" element={<PrivateRoute><ClientsPage /></PrivateRoute>} />
      <Route path="/products" element={<PrivateRoute><ProductsPage /></PrivateRoute>} />
      <Route path="/rentals" element={<PrivateRoute><AlquileresPage /></PrivateRoute>} />
      <Route path="/rentals/new" element={<PrivateRoute><RentalBuilder /></PrivateRoute>} />
      <Route path="/rentals/:id/edit" element={<PrivateRoute><RentalBuilder /></PrivateRoute>} />
      <Route path="/rentals/:id/print" element={<PrivateRoute><RentalContractPrintView /></PrivateRoute>} />
      <Route path="/whatsapp" element={<PrivateRoute><WhatsAppInboxPage /></PrivateRoute>} />
      <Route path="/expenses" element={<PrivateRoute allowedRoles={['admin']}><GastosPage /></PrivateRoute>} />
      <Route path="/quotes" element={<PrivateRoute><QuotesPage /></PrivateRoute>} />
      <Route path="/quotes/new" element={<PrivateRoute><QuoteBuilder /></PrivateRoute>} />
      <Route path="/quotes/:id/edit" element={<PrivateRoute><QuoteBuilder /></PrivateRoute>} />
      <Route path="/quotes/:id/print" element={<PrivateRoute><QuotePrintView /></PrivateRoute>} />
      <Route path="/conduces" element={<PrivateRoute><ConducesPage /></PrivateRoute>} />
      <Route path="/conduces/new" element={<PrivateRoute><ConduceBuilder /></PrivateRoute>} />
      <Route path="/conduces/:id/edit" element={<PrivateRoute><ConduceBuilder /></PrivateRoute>} />
      <Route path="/conduces/:id/print" element={<PrivateRoute><ConducePrintView /></PrivateRoute>} />
      <Route path="/ingresos/:id/print" element={<PrivateRoute><IngresoPrintView /></PrivateRoute>} />
      <Route path="/invoices" element={<PrivateRoute><InvoicesPage /></PrivateRoute>} />
      <Route path="/invoices/new" element={<PrivateRoute><InvoiceBuilder /></PrivateRoute>} />
      <Route path="/invoices/:id/edit" element={<PrivateRoute><InvoiceBuilder /></PrivateRoute>} />
      <Route path="/invoices/:id/print" element={<PrivateRoute><InvoicePrintView /></PrivateRoute>} />
      <Route path="/invoices/:id/preview" element={<PrivateRoute><InvoicePrintView preview /></PrivateRoute>} />
      <Route path="/ingresos" element={<PrivateRoute><IngresosPage /></PrivateRoute>} />
      <Route path="/aprobaciones" element={<PrivateRoute><ApprovalsPage /></PrivateRoute>} />
      <Route path="/reports" element={<PrivateRoute><ReportsPage /></PrivateRoute>} />
      <Route path="/reports/print" element={<PrivateRoute><ReportPrintView /></PrivateRoute>} />
      <Route path="/admin" element={<PrivateRoute allowedRoles={['admin']}><AdminDashboard /></PrivateRoute>} />
      <Route path="/change-password" element={<PrivateRoute><ChangePasswordPage /></PrivateRoute>} />

      <Route path="*" element={<Navigate to={isLoggedIn ? '/dashboard' : '/login'} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <ActivityBar />
          <ReasonPrompt />
          <ApprovalProvider>
            <Header />
            <LockdownBanner />
            <AppRoutes />
          </ApprovalProvider>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
